import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { WEALTH_SERVICES, RISK_SERVICES, FINANCE_SERVICES } from '../data/siteData';
import './Navbar.css';
import Icon from './Icon';
import logo from '../assets/logo.png';

const DROPDOWNS = [
  {
    label: 'Wealth Management',
    path: '/wealth-management',
    color: 'var(--green)',
    services: WEALTH_SERVICES,
    basePath: '/service/wealth',
    viewAllText: 'View all Wealth Management →', // Added explicit text
  },
  {
    label: 'Risk Management',
    path: '/risk-management',
    color: 'var(--red)',
    services: RISK_SERVICES,
    basePath: '/service/risk',
    viewAllText: 'View all Risk Management →',
  },
  {
    label: 'Finance Management',
    path: '/finance-management',
    color: '#1a56a0',
    services: FINANCE_SERVICES,
    basePath: '/service/finance',
    viewAllText: 'View all Finance Management →',
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(null);
  }, [location]);

  const handleMouseEnter = (label) => {
    clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="nav-inner">
        <Link to="/" className="nav-logo">
          <div className="logo-icon">
            <img src={logo} alt="LMS Marketing & Services" className="logo-image" />
          </div>
        </Link>

        <ul className="nav-links">
          <li>
            <Link to="/" className={`nav-link${location.pathname === '/' ? ' active' : ''}`}>Home</Link>
          </li>
          {DROPDOWNS.map(dd => (
            <li key={dd.label} className="nav-dropdown-item"
              onMouseEnter={() => handleMouseEnter(dd.label)}
              onMouseLeave={handleMouseLeave}>
              <Link
                to={dd.path}
                className={`nav-link nav-link-arrow${location.pathname.startsWith(dd.path) || location.pathname.startsWith(dd.basePath) ? ' active' : ''}`}>
                {dd.label} <span className={`dd-chevron${openDropdown === dd.label ? ' open' : ''}`}>▾</span>
              </Link>
              <div className={`nav-dropdown${openDropdown === dd.label ? ' open' : ''}`}
                onMouseEnter={() => handleMouseEnter(dd.label)}
                onMouseLeave={handleMouseLeave}>
                <div className="dd-header" style={{ borderColor: dd.color }}>
                  <span style={{ color: dd.color }}>{dd.label}</span>
                </div>
                <div className="dd-grid">
                  {dd.services.map(s => (
                    <Link
                      key={s.id}
                      to={`${dd.basePath}/${s.id}`}
                      className="dd-item"
                      onClick={() => setOpenDropdown(null)}>
                      <span className="dd-item-icon"><Icon icon={s.icon} size={22} /></span>
                      <div>
                        <div className="dd-item-name">{s.short || s.name}</div>
                        <div className="dd-item-tag">{s.tagline}</div>
                      </div>
                    </Link>
                  ))}
                </div>
                {/* View All link - explicitly rendered for all dropdowns */}
                <Link to={dd.path} className="dd-view-all" style={{ color: dd.color }}>
                  {dd.viewAllText || `View all ${dd.label} →`}
                </Link>
              </div>
            </li>
          ))}
          <li>
            <Link to="/blogs" className={`nav-link${location.pathname === '/blogs' ? ' active' : ''}`}>Blog</Link>
          </li>
          <li>
            <Link to="/gallery" className={`nav-link${location.pathname === '/gallery' ? ' active' : ''}`}>Gallery</Link>
          </li>
          <li>
            <Link to="/about" className={`nav-link${location.pathname === '/about' ? ' active' : ''}`}>About</Link>
          </li>
        </ul>

        <Link to="/contact" className="nav-cta btn-primary">Free Consultation →</Link>
        <button className={`hamburger${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
          <span/><span/><span/>
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <Link to="/" className={`mobile-link${location.pathname === '/' ? ' active' : ''}`}>Home</Link>
        {DROPDOWNS.map(dd => (
          <div key={dd.label} className="mobile-dropdown">
            <button
              className={`mobile-link mobile-link-btn${mobileOpen === dd.label ? ' active' : ''}`}
              onClick={() => setMobileOpen(mobileOpen === dd.label ? null : dd.label)}>
              {dd.label} <span className={`dd-chevron${mobileOpen === dd.label ? ' open' : ''}`}>▾</span>
            </button>
            <div className={`mobile-sub${mobileOpen === dd.label ? ' open' : ''}`}>
              {dd.services.map(s => (
                <Link key={s.id} to={`${dd.basePath}/${s.id}`} className="mobile-sub-link">
                  <Icon icon={s.icon} size={18} /> {s.short || s.name}
                </Link>
              ))}
              <Link to={dd.path} className="mobile-sub-link mobile-sub-all" style={{ color: dd.color }}>
                → View All
              </Link>
            </div>
          </div>
        ))}
        <Link to="/blogs" className={`mobile-link${location.pathname === '/blogs' ? ' active' : ''}`}>Blog</Link>
        <Link to="/gallery" className={`mobile-link${location.pathname === '/gallery' ? ' active' : ''}`}>Gallery</Link>
        <Link to="/about" className={`mobile-link${location.pathname === '/about' ? ' active' : ''}`}>About</Link>
        <Link to="/contact" className="btn-primary mobile-cta">Free Consultation →</Link>
      </div>
    </nav>
  );
}